import { metadataTypeIgnoredFilters } from '@/components/Dashboard/Dashboard.utils';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Link from '@/components/UI/Link';
import { FilterTypes as filterType } from '@/constants/FilterTypes';
import { errorToast, fetchMetadataColumns, successToast } from '@/helpers';
import { _isEmpty, _isNil, _isNotEmpty, _notNil } from '@/littledash';
import { type ID } from '@/model/Common.model.ts';
import type { StudyApiId } from '@/model/Study.model';
import Http from '@/support/http';
import { api as apiRoute, web as webRoute } from '@/support/route';
import { selectOperations } from '@/utils/FiltersOperations.utils';
import { modalAction } from '@/utils/modal';
import { filterOptionsFromApi } from '../Colony.utils';
import CageActionsCell from '@/components/Colony/Cages/CageActions.tsx';
import { type Cage as BaseCage, type CageApiId } from '@/model/Cage.model';
import { type Dispatch } from 'react';
import type { NavigateFunction } from 'react-router-dom';

// Extended Cage type for Colony components with additional API fields
export interface Cage extends BaseCage {
  study_link?: {
    data?: {
      id: number;
      name: string;
      access?: string;
      deleted_at?: string;
      archived_at?: string | null;
      canceled_at?: string | null;
    };
  };
}

export interface CageFiltersEntity {
  fields: Array<{
    name: string;
    value: string;
    options: Array<Record<string, number>>;
  }>;
  metadata: Array<CageMetadataFilter>;
}

export interface CageMetadataFilter {
  id: ID;
  name: string;
  slug: string;
  field_type: string;
}

export const BULK_EDIT_CAGES_RETURN_TO_KEY = 'bulkEditCagesReturnTo';
export const SELECTED_COLONY_CAGES_KEY = 'selectedColonyCages';
const DEFAULT_BULK_EDIT_RETURN_TO = '/colony';

/**
 * Resolves a safe in-app return path after bulk cage edit.
 * Only relative same-origin paths are accepted.
 */
export const resolveBulkEditCagesReturnTo = (storedReturnTo: string | null): string => {
  if (_isNil(storedReturnTo) || !storedReturnTo.startsWith('/') || storedReturnTo.startsWith('//')) {
    return DEFAULT_BULK_EDIT_RETURN_TO;
  }
  return storedReturnTo;
};

export const colonyCagesBulkActions = (
  {
    useTableProps: {
      selectedFlatRows,
      apiTable: { refetchTableData },
    },
  }: {
    useTableProps: {
      selectedFlatRows: Array<{ original: Cage }>;
      apiTable: { refetchTableData: () => Promise<void> };
    };
  },
  handleDelete: (
    selectedCages: Array<Cage>,
    callback: (message: string) => Promise<void>,
    reasonForChange?: string
  ) => void,
  dispatch: Dispatch<{ type: string; data?: unknown }>,
  navigate: NavigateFunction,
  reasonForChangeEnabled: boolean = false
) => {
  const { openModal, closeModal } = modalAction(dispatch);
  const selectedTableRows = selectedFlatRows.map(({ original }) => original);
  const callback = async (text: string) => {
    closeModal();
    successToast(text);
    await refetchTableData();
  };
  return [
    {
      name: 'Manage cages',
      key: 'manage_cages',
      ...validateManageCagesSelection(selectedTableRows),
      action: () => {
        const cageApiIds = selectedTableRows.map((cage) => cage.api_id);
        const studyId = selectedTableRows[0]?.study_id ?? null;

        // Pass data via route state
        navigate(webRoute('colony.manage_cages'), {
          state: { cageApiIds, studyId },
        });
      },
    },
    {
      name: 'Edit cage details',
      key: 'edit_details',
      ...validateManageCagesSelection(selectedTableRows),
      action: () => {
        sessionStorage.setItem(SELECTED_COLONY_CAGES_KEY, JSON.stringify(selectedTableRows));
        navigate(webRoute('colony.edit.cages'));
      },
    },
    {
      name: 'Delete',
      ...validateManageCagesSelection(selectedTableRows),
      action: () =>
        openModal('CONFIRM_DELETE_COLLECTIONS', {
          subject: selectedTableRows,
          showReasonForChange: reasonForChangeEnabled,
          onClick: (reasonForChange?: string) => handleDelete(selectedTableRows, callback, reasonForChange),
        }),
      className: 'red',
    },
  ];
};

export const validateManageCagesSelection = (selectedCages: Array<Cage> = [], limit: number = 300) => {
  const defaultResult = {
    disabled: false,
    tooltip: '',
  };

  if (_isEmpty(selectedCages)) {
    return defaultResult;
  }

  const allowedAccessPermissions = ['write'];

  if (selectedCages.length > limit) {
    return {
      ...defaultResult,
      disabled: true,
      tooltip: `You can only manage ${limit} cages a time`,
    };
  }

  const studyLinkData = selectedCages[0].study_link?.data;
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  const cageInStudy = !!selectedCages[0].study_id;
  const cagesAssignedToSameStudy = selectedCages.every((cage) => cage.study_link?.data?.id === studyLinkData?.id);

  if (!cagesAssignedToSameStudy) {
    return {
      ...defaultResult,
      disabled: true,
      tooltip: 'All cages must be assigned to the same study',
    };
  }

  if (
    cageInStudy &&
    (_notNil(studyLinkData?.archived_at) || _notNil(studyLinkData?.canceled_at) || _notNil(studyLinkData?.deleted_at))
  ) {
    return {
      ...defaultResult,
      disabled: true,
      tooltip: 'You cannot manage cages belonging to an archived, cancelled, or deleted study',
    };
  }

  if (
    cageInStudy &&
    (_isNil(studyLinkData?.access) || !allowedAccessPermissions.includes(String(studyLinkData?.access ?? '')))
  ) {
    return {
      ...defaultResult,
      disabled: true,
      tooltip: 'You must have at least write access to the study to manage cages',
    };
  }

  return defaultResult;
};

export interface CageReorderSelectOption {
  value: CageApiId;
  label: string;
}

/**
 * Builds target-cage dropdown options by excluding the selected cages.
 */
export const buildCageReorderSelectOptions = (
  availableCages: Array<Pick<Cage, 'api_id' | 'name'>>,
  selectedCages: Array<Pick<Cage, 'api_id'>>
): Array<CageReorderSelectOption> => {
  const selectedIds = new Set(selectedCages.map((selectedCage) => selectedCage.api_id));
  return availableCages.reduce<Array<CageReorderSelectOption>>((acc, availableCage) => {
    if (!selectedIds.has(availableCage.api_id)) {
      acc.push({
        value: availableCage.api_id,
        label: availableCage.name,
      });
    }
    return acc;
  }, []);
};

/**
 * Shown when the study holds no cage that the selection could be moved relative to.
 * Covers both a single-cage study and a selection that already spans every cage.
 */
export const NO_REORDER_TARGET_TOOLTIP = 'Reordering needs another cage in this study to move relative to';

/**
 * Reorder-specific disable/tooltip rules on top of shared manage-cages selection validation.
 * Reorder is blocked only when no target cage is left, never on a raw cage count, so a
 * single selected cage stays reorderable.
 */
export const validateStudyCagesReorderSelection = (
  selectedCages: Array<Cage> = [],
  availableTargets: Array<CageReorderSelectOption> = [],
  selectionState: { disabled: boolean; tooltip: string } = { disabled: false, tooltip: '' }
) => {
  if (selectionState.disabled) {
    return selectionState;
  }

  if (_isEmpty(selectedCages)) {
    return {
      disabled: true,
      tooltip: 'Select at least one cage to reorder',
    };
  }

  if (_isEmpty(availableTargets)) {
    return {
      disabled: true,
      tooltip: NO_REORDER_TARGET_TOOLTIP,
    };
  }

  return {
    disabled: false,
    tooltip: '',
  };
};

/**
 * Loads all study cages for reorder target options (same filter pattern as card view).
 */
export const fetchStudyCagesForReorderOptions = async (studyId: string): Promise<Array<Cage>> => {
  const {
    data: { data },
  } = await Http.get<{ data: Array<Cage> }>(
    apiRoute(
      'cages.show',
      {},
      {
        sort: 'cage_order',
        order: 'asc',
        filterType: 'and',
        'filters[]=': `study|eq|${studyId}`,
        perPage: '-1',
      }
    )
  );
  return data ?? [];
};

/**
 * Study Cages list bulk actions (BNCH-130048): Edit details, Reorder, Delete.
 * Print remains a single-cage row action. Manage cages stays colony-only.
 */
export const studyCagesBulkActions = (
  {
    useTableProps: {
      selectedFlatRows,
      apiTable: { refetchTableData, setUpdating },
    },
  }: {
    useTableProps: {
      selectedFlatRows: Array<{ original: Cage }>;
      apiTable: {
        refetchTableData: () => Promise<void>;
        setUpdating: (updating: boolean) => void;
      };
    };
  },
  handleDelete: (
    selectedCages: Array<Cage>,
    callback: (message: string) => Promise<void>,
    reasonForChange?: string
  ) => void,
  dispatch: Dispatch<{ type: string; data?: unknown }>,
  navigate: NavigateFunction,
  reasonForChangeEnabled: boolean = false,
  studyId: string,
  studyApiId?: StudyApiId,
  studyCages: Array<Cage> = []
) => {
  const { openModal, closeModal } = modalAction(dispatch);
  const selectedTableRows = selectedFlatRows.map(({ original }) => original);
  const callback = async (text: string) => {
    closeModal();
    successToast(text);
    await refetchTableData();
  };
  const selectionState = validateManageCagesSelection(selectedTableRows);
  // Recomputed on every render, so it tracks the live table selection rather than a
  // count captured when the table last fetched.
  const availableReorderTargets = buildCageReorderSelectOptions(studyCages, selectedTableRows);
  const reorderSelectionState = validateStudyCagesReorderSelection(
    selectedTableRows,
    availableReorderTargets,
    selectionState
  );

  return [
    {
      name: 'Edit cage details',
      key: 'edit_details',
      ...selectionState,
      action: () => {
        sessionStorage.setItem(SELECTED_COLONY_CAGES_KEY, JSON.stringify(selectedTableRows));
        sessionStorage.setItem(BULK_EDIT_CAGES_RETURN_TO_KEY, webRoute('studies.cages', { id: studyId }));
        navigate(webRoute('colony.edit.cages'));
      },
    },
    {
      name: 'Reorder',
      key: 'reorder',
      ...reorderSelectionState,
      action: async () => {
        if (reorderSelectionState.disabled) {
          return;
        }

        setUpdating(true);
        try {
          let resolvedStudyApiId = studyApiId;
          if (_isNil(resolvedStudyApiId)) {
            const {
              data: { data: study },
            } = await Http.get<{ data: { api_id: StudyApiId } }>(
              apiRoute('studies.show', { id: studyId, include: '' })
            );
            resolvedStudyApiId = study.api_id;
          }

          // Re-read the study cages on open so the dropdown reflects current names and order.
          const latestStudyCages = await fetchStudyCagesForReorderOptions(studyId);
          const selectOptions = buildCageReorderSelectOptions(latestStudyCages, selectedTableRows);

          if (_isEmpty(selectOptions)) {
            errorToast('No target cage available for reorder');
            return;
          }

          openModal('REORDER_CAGE', {
            cages: selectedTableRows,
            selectOptions,
            studyApiId: resolvedStudyApiId,
            fetchCages: refetchTableData,
          });
        } catch {
          errorToast('Failed to load cages for reorder');
        } finally {
          setUpdating(false);
        }
      },
    },
    {
      name: 'Delete',
      ...selectionState,
      action: () =>
        openModal('CONFIRM_DELETE_COLLECTIONS', {
          subject: selectedTableRows,
          showReasonForChange: reasonForChangeEnabled,
          onClick: (reasonForChange?: string) => handleDelete(selectedTableRows, callback, reasonForChange),
        }),
      className: 'red',
    },
  ];
};

export const generateColumns = (
  { metadata }: { metadata: Array<CageMetadataFilter> },
  actions: Array<unknown>,
  addToStudy: boolean,
  dispatch: Dispatch<CagesAction>,
  storeDispatch: Dispatch<{ type: string; data?: unknown }>,
  handleRemoveCageFromStudy: (apiId: CageApiId, name: string) => Promise<void>,
  /**
   * Temporary Part 1/2 flag (BNCH-130048): Study Cages list view reuse of Colony columns.
   * Hides Study column. Actions column is print-only in studyScoped mode.
   */
  studyScoped = false
) => {
  const hideStudyColumn = addToStudy || studyScoped;
  let result = [
    {
      id: 'id',
      accessorKey: 'id',
      isVisible: false,
    },
    {
      id: 'catalog',
      header: 'Cage No.',
      accessorKey: 'catalog',
      enableSorting: true,
      isVisible: true,
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      cell: ({ row: { original } }: { row: { original: Cage } }) => original.catalog || '',
    },
    ...(studyScoped
      ? [
          {
            id: 'cage_order',
            header: 'Cage order',
            accessorKey: 'cage_order',
            enableSorting: true,
            isVisible: true,
            cell: ({ row: { original } }: { row: { original: Cage } }) =>
              _notNil(original.cage_order) ? original.cage_order : '',
          },
        ]
      : []),
    {
      id: 'name',
      header: 'Name',
      accessorKey: 'name',
      enableSorting: true,
      isVisible: true,
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      cell: ({ row: { original } }: { row: { original: Cage } }) => original.name || '',
    },
    {
      id: 'population',
      header: 'Population',
      enableSorting: false,
      accessorFn: (row: Cage) => row.counts?.animals,
      isVisible: true,
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      cell: ({ row: { original } }: { row: { original: Cage } }) => original.counts?.animals || 0,
    },
  ];

  if (!hideStudyColumn) {
    result = [
      ...result,
      {
        id: 'study',
        header: 'Study',
        accessorKey: 'study_id',
        enableSorting: true,
        isVisible: true,
        cell: ({ row: { original } }: { row: { original: Cage } }) => {
          const studyData = original.study_link?.data;
          const studyName = studyData?.name ?? '';
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          return studyData?.access && _isNil(studyData.deleted_at) ? (
            <Link className="link blue" to={webRoute('studies.show', { id: studyData.id })}>
              {studyName}
            </Link>
          ) : _isNil(studyData?.deleted_at) ? (
            studyName
          ) : (
            studyName + ' (Deleted)'
          );
        },
      } as any,
    ];
  }

  result = [
    ...result,
    {
      id: 'date',
      header: 'Created',
      accessorKey: 'created_at',
      enableSorting: true,
      isVisible: true,
      cell: ({ row: { original } }: { row: { original: Cage } }) => <DateTimeRenderer value={original.created_at} />,
    } as any,
    ...(fetchMetadataColumns({ metadata: metadata as any }) as any),
  ];

  if (!addToStudy) {
    result = [
      ...result,
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        accessorKey: 'actions',
        isVisible: true,
        cell: ({ row }: { row: { original: Cage } }) => (
          <CageActionsCell
            cage={row.original as any}
            dispatch={dispatch as any}
            storeDispatch={storeDispatch as any}
            handleRemoveCageFromStudy={handleRemoveCageFromStudy}
            printOnly={studyScoped}
            isInSideStudyTable={studyScoped}
          />
        ),
      } as any,
    ];
  }

  return result;
};

export const generateMetadataFilterOptions = (metadata: Array<CageMetadataFilter>) => {
  const result: Array<{
    value: ID;
    name: string;
    operations: Array<{ name: string; value: string }>;
    fetchFromApi?: {
      placeholder: string;
      url: string;
      params: { metadataGlossaryId: ID };
      fieldType: string;
      useModernApi: boolean;
    };
  }> = [];
  metadata?.forEach(({ field_type, id, name }) => {
    if (!metadataTypeIgnoredFilters.includes(field_type)) {
      if (field_type === 'multi_select') {
        result.push({
          value: id,
          name,
          operations: selectOperations,
          fetchFromApi: {
            placeholder: 'Multiselect options',
            url: 'GET /api/v1/metadata-glossary/{metadataGlossaryId}/used-options',
            params: { metadataGlossaryId: id },
            fieldType: 'multi_select',
            useModernApi: true,
          },
        });
      } else if (field_type === 'select') {
        result.push({
          value: id,
          name,
          operations: selectOperations,
          fetchFromApi: {
            placeholder: 'Select options',
            url: 'GET /api/v1/metadata-glossary/{metadataGlossaryId}/used-options',
            params: { metadataGlossaryId: id },
            fieldType: 'select',
            useModernApi: true,
          },
        });
      } else {
        result.push({
          value: id,
          name,
          operations: [
            {
              name: filterType.contains,
              value: field_type === 'date' ? filterType.eq_date : filterType.contains,
            },
            {
              name: filterType.not_contains,
              value: field_type === 'date' ? filterType.not_eq_date : filterType.not_contains,
            },
          ],
        });
      }
    }
  });
  return result;
};

export const generateFilterOptions = (filters: CageFiltersEntity, hideStudyFilter: boolean) => {
  const result = [
    {
      value: 'animals.count',
      name: 'Population',
      operations: [
        {
          name: 'greater than',
          value: filterType.gt,
        },
        {
          name: 'less than',
          value: filterType.lt,
        },
        {
          name: 'empty',
          value: filterType.EMPTY,
        },
      ],
    },
  ];

  const metadataFilterOptions = generateMetadataFilterOptions(filters.metadata);
  if (_isNotEmpty(metadataFilterOptions)) {
    result.push(...(metadataFilterOptions as any));
  }

  if (_isNotEmpty(filters.fields)) {
    const filteredFields = hideStudyFilter ? filters.fields.filter((field) => field.name !== 'Study') : filters.fields;

    filteredFields.forEach((field) => {
      const { name, value, options } = field;
      const fetchFromApi = filterOptionsFromApi(name);
      if (fetchFromApi) {
        result.push({
          value,
          name,
          operations: [
            {
              name: 'is equal to',
              value: filterType.eq,
              options: [] as any,
            } as any,
            {
              name: 'is not equal to',
              value: filterType.ne,
              options: [] as any,
            } as any,
          ],
          fetchFromApi: {
            placeholder: 'Search studies',
            url: 'studies.base',
          },
        } as any);
      } else {
        result.push({
          value,
          name,
          operations: [
            {
              name: 'is equal to',
              value: filterType.eq,
              options: options as any,
            } as any,
            {
              name: 'is not equal to',
              value: filterType.ne,
              options: options as any,
            } as any,
          ],
        });
      }
    });
  }

  return result;
};

/**
 * Reducer for Cages component state.
 * Note: After migration to TanStack Table v8, many states are now managed by the table itself.
 * This reducer only handles states that are not managed by TanStack Table.
 */
export interface CagesState {
  updating: boolean;
  apiErrors: boolean;
  data: Array<Cage>;
  columns: Array<unknown>;
  searchQuery: string;
  filterOptions: Array<unknown>;
  filters: Array<unknown>;
  filterMatch: boolean;
  selectedRows: Record<string, unknown>;
  total_pages: number;
  current_page: number;
  per_page: number;
  order: 'asc' | 'desc';
  sort: Array<string>;
  cageIdToPrint: number | string | null;
  actions: Array<unknown>;
  metadata: Array<unknown>;
}

export type CagesAction =
  | { type: 'SET_UPDATING'; data: boolean }
  | { type: 'SET_ERROR'; data: boolean }
  | { type: 'SET_COLUMNS'; data: Array<unknown> }
  | { type: 'SET_FILTER_OPTIONS'; data: Array<unknown> }
  | { type: 'SET_CAGE_ID_TO_PRINT'; data: number | string | null }
  | { type: 'SET_ACTIONS'; data: Array<unknown> }
  | { type: 'SET_METADATA'; data: Array<unknown> };

export const reducer = (state: CagesState, action: CagesAction) => {
  switch (action.type) {
    // Actions data from API
    case 'SET_ACTIONS':
      return {
        ...state,
        actions: action.data,
      };

    // Loading/updating states
    case 'SET_UPDATING':
      return {
        ...state,
        updating: action.data,
      };

    // Error states
    case 'SET_ERROR':
      return {
        ...state,
        apiErrors: action.data,
      };

    // Filter options from API
    case 'SET_FILTER_OPTIONS':
      return {
        ...state,
        filterOptions: action.data,
      };
    case 'SET_METADATA':
      return {
        ...state,
        metadata: action.data,
      };
    case 'SET_COLUMNS': {
      return {
        ...state,
        columns: action.data,
      };
    }

    // Cage ID for printing
    case 'SET_CAGE_ID_TO_PRINT':
      return {
        ...state,
        cageIdToPrint: action.data,
      };

    //
    // REMOVED ACTIONS (now managed by TanStack Table v8):
    // - SET_SEARCH_QUERY (handled by table search state)
    // - SET_SELECTED (handled by table rowSelection state)
    // - SET_PAGING (handled by table pagination state)
    // - SET_FILTERS (handled by table filtering)
    // - SET_SORT (handled by table sorting state)
    //

    default: {
      return { ...state };
    }
  }
};
